<template>
  <!--begin::Users index for Superadmin role-->
  <div>
    <div class="manager-title">
      <inline-svg
        class="manager-title-icon"
        src="/media/icons/usuarios-primary.svg"
      />
      <div>
        {{ $t("USERS.TITLE_INDEX") }}
        <hr class="manager-title-sub" />
      </div>
    </div>

    <!--begin::Row Filters-->
    <div class="white-box mb-9">
      <div class="row py-6 align-items-center">
        <div class="col-md-6 d-flex d-row align-items-center">
          <h5 class="mr-3 mb-0 font-weight-bolder" style="white-space: pre">
            {{ $t("USERS.SEARCH") }}
          </h5>
          <v-text-field
            class="form-control search-text"
            v-model.lazy="SearchText"
            :placeholder="$t('GENERIC.SEARCH') + '...'"
            @keyup="onSearchTextKeyup"
          >
            <template v-slot:prepend-inner>
              <inline-svg src="/media/icons/search-alt-1-primary.svg" />
            </template>
          </v-text-field>
        </div>
        <div class="col-md-6">
          <span
            class="action-icon"
            :class="[ShowFilters ? 'bg-primary' : '']"
            @click="() => (ShowFilters = !ShowFilters)"
          >
            <inline-svg v-if="ShowFilters" src="/media/icons/filter-alt-1.svg" />
            <inline-svg v-else src="/media/icons/filter-alt-1-primary.svg" />
          </span>
        </div>
      </div>

      <div v-if="ShowFilters" class="row py-6 box-filters">
        <div class="col-lg-3">
          <label>
            {{ $t("MY_PROFILE.ROLE") }}
          </label>
          <select
            class="form-control select-down"
            v-model="FilterByRoleID"
            @change="searchUsers(false)"
          >
            <option
              v-for="r in rolesList"
              :key="r"
              :value="r"
              class="text-primary"
            >
              {{ $t("ROLE_NAMES_BY_ID." + r) }}
            </option>
          </select>
        </div>

        <div class="col-lg-9 pt-7 pl-9">
          <button
            class="btn btn-pill btn-white button-form button-form-primary"
            @click="cleanFilters"
          >
            <inline-svg src="/media/icons/cross-primary.svg" />
            {{ $t("GENERIC.CLEAN_FILTERS") }}
          </button>
        </div>
      </div>
    </div>
    <!--end::Row Filters-->

    <!--begin::Row Searching-->
    <div v-if="ItemsSearching" class="search-info">
      <inline-svg class="mx-3" src="/media/icons/help-alt-5-success.svg" />
      {{ $t("GENERIC.SEARCHING") }}...
    </div>
    <!--end::Row Searching-->

    <!--begin::Table Results-->
    <v-data-table
      v-if="ItemsList.length > 0"
      class="white-table shadow-box"
      :headers="TableHeaders"
      :items="ItemsList"
      :items-per-page="ItemsPerPage"
      :options.sync="options"
      :server-items-length="ItemsTotal"
      @update:sort-by="updateSortBy"
      @update:sort-desc="updateSortDesc"
      hide-default-footer
    >
      <template v-slot:item="{ item }">
        <tr @click="$router.push('/manager/users/edit/' + item.UserID)">
          <td>
            {{ formatDatetimeWithoutZone(item.CreatedAt) }}
          </td>
          <td>
            {{ item.Email }}
          </td>
          <td>
            {{ formatRolesNames(item.RolesID) }}
          </td>
          <td>
            <span class="action-icon">
              <inline-svg src="/media/icons/edit-alt-2-primary.svg" />
            </span>
          </td>
        </tr>
      </template>
    </v-data-table>
    <!--end::Table Results-->

    <!--begin::Row Pagination-->
    <div v-if="!ItemsSearching && ItemsList.length > 0" class="p-0 row">
      <div
        class="p-4 d-flex justify-content-between align-items-center flex-wrap w-100"
      >
        <div>
          {{
            $t("GENERIC.PAGINATION_SHOW", {
              from: ItemsFrom,
              to: ItemsTo,
              total: ItemsTotal
            })
          }}
        </div>

        <v-pagination
          v-model="ItemsCurrentPage"
          :length="ItemsPages"
          @input="searchUsers(true)"
          :previous-aria-label="$t('GENERIC.PREVIOUS')"
          :next-aria-label="$t('GENERIC.NEXT')"
        />

        <div class="d-flex align-items-baseline py-3">
          <v-select
            class="form-control form-control-sm mr-0 border-0 bg-light"
            style="max-width: 5em;"
            v-model="ItemsPerPage"
            single-line
            :items="ItemsPerPageOptions"
            @change="updateItemsPerPage"
          />
          <span>{{ $t("GENERIC.PAGINATION_MENU") }}</span>
        </div>
      </div>
    </div>
    <!--end::Row Pagination-->

    <!--begin::Row Zero-->
    <div v-if="!ItemsSearching && ItemsList.length < 1" class="search-info">
      <inline-svg class="mx-3" src="/media/icons/help-alt-5-success.svg" />
      {{ $t("GENERIC.NO_RESULTS") }}
    </div>
    <!--end::Row Zero-->
    <!--end::Users index for Superadmin role-->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME
} from "@/core/services/store/htmlclass.module.js";
import UserService from "@/core/services/api/v2/user.service";

export default {
  name: "UsersSuperadminIndex",
  computed: {
    ...mapGetters(["currentUserRoleID", "rolesList"])
  },
  data() {
    return {
      TableHeaders: [
        {
          text: this.$t("GENERIC.CREATED_AT"),
          value: "CreatedAt"
        },
        { text: this.$t("MY_PROFILE.EMAIL"), value: "Email" },
        { text: this.$t("MY_PROFILE.ROLE"), value: "RolesID", sortable: false },
        { text: this.$t("GENERIC.ACTIONS"), width: 120, sortable: false }
      ],
      options: {},
      SortBy: "CreatedAt",
      SortDesc: true,
      RoleOptions: [],
      RoleNames: [],
      ItemsList: [],
      ItemsSearching: true,
      DebounceSearchText: null,
      SearchText: "",
      ShowFilters: false,
      FilterByRoleID: 0,
      ItemsFrom: 0,
      ItemsTo: 0,
      ItemsTotal: 0,
      ItemsPerPage: 5,
      ItemsPages: 1,
      ItemsCurrentPage: 1,
      ItemsPerPageOptions: [
        { text: 5, value: 5 },
        { text: 10, value: 10 },
        { text: 25, value: 25 },
        { text: 50, value: 50 }
      ]
    };
  },
  created() {
    // Show page loader
    this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
  },
  mounted() {
    this.loadCachedFilters();

    // Remove page loader
    this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");

    // Do first search
    this.searchUsers(false);
  },
  methods: {
    loadCachedFilters() {
      let anyFilterNotEmpty = false;

      Object.entries(UserService.getCachedFiltersUsers()).forEach(f => {
        this["FilterBy" + f[0]] = f[1];

        if (!anyFilterNotEmpty) {
          anyFilterNotEmpty = f[1].toString().length > 0;
        }
      });

      this.ShowFilters = anyFilterNotEmpty;
    },

    searchUsers(keepCurrentPage) {
      this.ItemsSearching = true;
      this.ItemsList = [];

      // Compute pagination params
      if (!keepCurrentPage) {
        this.ItemsCurrentPage = 1;
      }

      const limit = this.ItemsPerPage;
      const offset =
        this.ItemsCurrentPage === 0 ? 0 : limit * (this.ItemsCurrentPage - 1);

      UserService.searchUsers(
        this.SearchText,
        this.FilterByRoleID,
        this.SortBy,
        this.SortDesc,
        limit,
        offset
      ).then(rawResponse => {
        if (rawResponse === 404) {
          this.ItemsList = [];
          this.ItemsPages = 1;
        } else {
          this.ItemsList = rawResponse.response.Users;

          this.ItemsTotal = rawResponse.total;
          this.ItemsPages = Math.ceil(this.ItemsTotal / this.ItemsPerPage);
          this.ItemsFrom = (this.ItemsCurrentPage - 1) * this.ItemsPerPage + 1;
          this.ItemsTo = this.ItemsFrom + this.ItemsList.length - 1;
        }

        this.ItemsSearching = false;
      });
    },

    updateSortBy(event) {
      if (event.length > 0) {
        this.SortBy = event[0];
        this.SortDesc = false;
        this.ItemsCurrentPage = 1;
        this.searchUsers(false);
      }
    },
    updateSortDesc(event) {
      this.SortDesc = event.length > 0 ? event[0] : this.SortDesc;
      this.ItemsCurrentPage = 1;
      this.searchUsers(false);
    },
    updateItemsPerPage(newValue) {
      this.ItemsPerPage = newValue;
      this.ItemsCurrentPage = 1;
      this.searchUsers(false);
    },

    changeItemsCurrentPage() {
      this.searchUsers(true);
    },

    cleanFilters() {
      this.FilterByRoleID = 0;
      this.ShowFilters = false;
      this.searchUsers(false);
    },

    onSearchTextKeyup() {
      clearTimeout(this.DebounceSearchText);
      this.DebounceSearchText = setTimeout(() => {
        this.searchUsers(false);
      }, 600);
    },

    formatDatetimeWithoutZone(datetime) {
      return moment(datetime).format("DD/MM/YYYY HH:mm");
    },

    formatRolesNames(rolesID) {
      let names = [];

      rolesID.forEach(r => {
        names.push(this.$i18n.t("ROLE_NAMES_BY_ID." + r));
      });

      return names.join(", ");
    }
  }
};
</script>
